import {
  CheckIcon,
  CloseIcon,
  Col,
  DeauthoriseIcon,
  ErrorDialog,
  Grid,
  PendingIcon,
  Row,
  Typography,
  UnlinkIcon,
} from "@codat/orchard-ui";
import { useGetConnectionsReportQuery } from "api/portal";
import classNames from "classnames";
import { BannerType, useBannerContext } from "contexts/BannerContext";
import { usePageWidthViewContext } from "contexts/PageWidthViewContext";
import { differenceInDays } from "date-fns";
import useLocalStorage from "hooks/useLocalStorage";
import pluralize from "pluralize";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useEffect, useMemo } from "react";
import { dashboardSupportedProductIds } from "utils";
import formatUrl from "utils/formatUrl";
import getTotalConnections from "utils/getTotalConnections";

import { paths } from "components/App/paths";
import Button from "components/Button";
import CreatedCompaniesChart from "components/CreatedCompaniesChart";
import { DashboardPill } from "components/DashboardPill";
import { createNavPermissionMap } from "components/NavContainer/utils";
import Page from "components/Page";

import UpgradeButton from "../../components/UpgradeButton";
import { useProducts } from "../../contexts/ProductsContext";
import ActiveCompaniesChart from "./ActiveCompaniesChart";
import styles from "./Home.module.scss";
import { IntroductionContent } from "./IntroductionContent";
import { IntroductionContentOld } from "./IntroductionContentOld";
import MigrationBanner from "./MigrationBanner";

const Home: React.FC = () => {
  const { addBanner, removeBanner, banners } = useBannerContext();
  const { userState } = useUserState();
  const [
    { loading: connectionsReportLoading, data: connectionsReport, error },
  ] = useGetConnectionsReportQuery();
  const { featureStatus } = useFeatureStatus();
  const { products } = useProducts();

  const { fullWidthView } = usePageWidthViewContext();

  const userCanViewAPIKeys =
    userState?.userPermissions.includes("apiKeys_view") ?? false;
  const userHasConnectedCompanies = getTotalConnections(connectionsReport) >= 2;

  const isMissedPaymentBannerDisplayed = useMemo(
    () => banners.some((x) => x.type === BannerType.MISSED_PAYMENT),
    [banners]
  );

  const navPermissionMap = useMemo(
    () =>
      createNavPermissionMap(
        userState ? userState.userPermissions : [],
        userState?.userRole
      ),
    [userState]
  );

  const hideStartupPlan = featureStatus?.["PortalUI.HideStartUpPlan"] ?? false;
  const showNewIntroduction = featureStatus?.["PortalUI.NewOnboardingFlow"];
  const enabledProducts = products?.filter(
    (p) =>
      p.status === "Enabled" &&
      dashboardSupportedProductIds.includes(p.productId)
  );

  useEffect(() => {
    if (
      !isMissedPaymentBannerDisplayed &&
      userState?.expiry &&
      userState?.clientType !== "ClientStartUp"
    ) {
      const daysToExpiry = differenceInDays(
        new Date(userState.expiry),
        new Date()
      );

      if (daysToExpiry <= 15) {
        addBanner({
          type: BannerType.ACCOUNT_EXPIRY_UPCOMING,
          index: 2,
          value: {
            title: `This account will expire in ${daysToExpiry} ${pluralize(
              "day",
              daysToExpiry
            )}`,
            variant: daysToExpiry < 7 ? "error" : "warning",
            cta: (
              <UpgradeButton
                userState={userState}
                hideStartUpPlan={hideStartupPlan}
              />
            ),
          },
        });

        return () => removeBanner(BannerType.ACCOUNT_EXPIRY_UPCOMING);
      }
    }
  }, [
    userState,
    addBanner,
    removeBanner,
    isMissedPaymentBannerDisplayed,
    navPermissionMap,
    hideStartupPlan,
  ]);

  const [displayMigrationBanner, setDisplayMigrationBanner] = useLocalStorage(
    "displayMigrationBanner",
    true
  );

  const isStartUpAccount = userState?.clientType === "ClientStartUp";
  const canViewBillingPage =
    isStartUpAccount && navPermissionMap.settings.billing.permission;

  if (error) {
    return (
      <ErrorDialog
        statusCode={error.response?.data?.statusCode}
        message={error.response?.data?.error}
        correlationId={error.response?.data?.correlationId}
      />
    );
  }

  const displayIntroductionContent = !!(
    showNewIntroduction && !connectionsReportLoading
  );

  return (
    <Page>
      {userState?.userRole &&
        ["Administrator", "Developer"].includes(userState.userRole) &&
        displayMigrationBanner && (
          <MigrationBanner
            onError={() => setDisplayMigrationBanner(true)}
            onClose={() => setDisplayMigrationBanner(false)}
          />
        )}
      <Grid fullWidth={fullWidthView} data-testId="home-page-content">
        {!showNewIntroduction && (
          <IntroductionContentOld userCanViewAPIKeys={userCanViewAPIKeys} />
        )}
        {(connectionsReportLoading || userHasConnectedCompanies) && (
          <>
            <Row className={classNames(styles.row, styles.dashboardHeadingRow)}>
              <Col size={12} className={styles.column}>
                <Typography
                  as="h2"
                  variant="heading_lg"
                  className={styles.subHeading}
                  // E2E tests check for this selector to determine whether they have reached the page successfully
                  data-testid="page-title"
                  isSkeleton={connectionsReportLoading}
                >
                  Dashboard
                </Typography>
              </Col>
              <Col
                size={12}
                className={classNames(styles.column, styles.linkContainer)}
              >
                {canViewBillingPage && (
                  <Button
                    to={paths.settings.USAGE}
                    color="interface"
                    variant="secondary"
                    size="small"
                    buttonName="View Usage"
                  >
                    View usage
                  </Button>
                )}
                <Button
                  to={paths.COMPANIES}
                  color="interface"
                  variant="secondary"
                  size="small"
                  buttonName="View All Companies"
                >
                  View companies
                </Button>
              </Col>
            </Row>
            <Row>
              {canViewBillingPage && (
                <Col size={6}>
                  <div
                    className={styles.homepageCharts}
                    data-testid="active-companies-chart-wrapper"
                  >
                    <ActiveCompaniesChart />
                  </div>
                </Col>
              )}
              <Col size={canViewBillingPage ? 6 : 12}>
                <div
                  className={styles.homepageCharts}
                  data-testid="created-companies-chart-wrapper"
                >
                  <CreatedCompaniesChart />
                </div>
              </Col>
            </Row>
            <Row collapseMargin className={classNames(styles.connectionsRow)}>
              <Col size={12}>
                <Typography as="label" className={styles.connectionsTitle}>
                  Company connections
                </Typography>
              </Col>
            </Row>
            <Row className={styles.pillRow}>
              <Col size={3}>
                <DashboardPill
                  title="Linked"
                  href={formatUrl(
                    paths.COMPANIES,
                    {},
                    { dataConnectionStatus: "Linked" }
                  )}
                  icon={<CheckIcon />}
                  count={connectionsReport?.linked?.toLocaleString()}
                  isSkeleton={connectionsReportLoading || !connectionsReport}
                  data-testid="linked-connections-dashboard-pill"
                  color="var(--color-ui-positive-dark)"
                  backgroundColor="var(--color-ui-positive-10)"
                  borderColor="var(--color-ui-positive-20)"
                  borderColorOnHover="var(--color-ui-positive)"
                  className={styles.pillWithLink}
                />
              </Col>
              <Col size={3}>
                <DashboardPill
                  title="Deauthorized"
                  href={formatUrl(
                    paths.COMPANIES,
                    {},
                    { dataConnectionStatus: "Deauthorized" }
                  )}
                  icon={<DeauthoriseIcon />}
                  count={connectionsReport?.deAuthorized?.toLocaleString()}
                  isSkeleton={connectionsReportLoading || !connectionsReport}
                  data-testid="deauthorized-connections-dashboard-pill"
                  color="var(--color-ui-warning-dark)"
                  backgroundColor="var(--color-ui-warning-10)"
                  borderColor="var(--color-ui-warning-20)"
                  borderColorOnHover="var(--color-ui-warning)"
                />
              </Col>
              <Col size={3}>
                <DashboardPill
                  title="Pending auth"
                  href={formatUrl(
                    paths.COMPANIES,
                    {},
                    { dataConnectionStatus: "PendingAuth" }
                  )}
                  icon={<PendingIcon />}
                  count={connectionsReport?.pendingAuth?.toLocaleString()}
                  isSkeleton={connectionsReportLoading || !connectionsReport}
                  data-testid="pending-auth-connections-dashboard-pill"
                  color="var(--color-ui-action-dark)"
                  backgroundColor="var(--color-ui-action-10)"
                  borderColor="var(--color-ui-action-20)"
                  borderColorOnHover="var(--color-ui-action)"
                />
              </Col>
              <Col size={3}>
                <DashboardPill
                  title="No connections"
                  icon={<CloseIcon className={styles.noConnectionsIcon} />}
                  count={connectionsReport?.noConnection?.toLocaleString()}
                  isSkeleton={connectionsReportLoading || !connectionsReport}
                  data-testid="no-connections-dashboard-pill"
                  color="var(--color-ui-interface-mid)"
                  backgroundColor="var(--color-ui-interface-10)"
                  borderColor="var(--color-ui-interface-20)"
                />
              </Col>
              <Col size={3}>
                <DashboardPill
                  title="Unlinked"
                  href={formatUrl(
                    paths.COMPANIES,
                    {},
                    { dataConnectionStatus: "Unlinked" }
                  )}
                  icon={<UnlinkIcon />}
                  count={connectionsReport?.unlinked?.toLocaleString()}
                  isSkeleton={connectionsReportLoading || !connectionsReport}
                  data-testid="unlinked-connections-dashboard-pill"
                  color="var(--color-ui-interface-dark)"
                  borderColor="var(--color-ui-interface-30)"
                  borderColorOnHover="var(--color-ui-interface)"
                />
              </Col>
            </Row>
          </>
        )}
        {displayIntroductionContent && (
          <IntroductionContent
            products={enabledProducts}
            userHasConnectedCompanies={userHasConnectedCompanies}
          />
        )}
      </Grid>
    </Page>
  );
};

export default Home;
